import { bisectLeft, bisectRight, ScaleTime } from "d3"
import { CompositePartTraceConfigJSON, TraceDataConfig } from "../Types/Trace"
import { TimeSeriesPageManager } from "./TimeSeriesPageManager"
import { Page } from "./Page"

export type TraceData = TimeSeriesData | CompositeTimeSeriesData

export type TimeSeriesData = {
    times: number[]
    data: Float32Array
}

export type CompositeTimeSeriesData = {
    times: number[]
    data: Float32Array[]
}

export function getSignal(traceData: TraceData, config: TraceDataConfig): Float32Array {
    if (config.isCompositePart && isCompositeTimeSeriesData(traceData)) {
        return traceData.data[config.compositeIndex]
    }

    return (traceData as TimeSeriesData).data
}

export function getTimeSeriesData(traceData: TimeSeriesData | CompositeTimeSeriesData, config: TraceDataConfig): TimeSeriesData {
    if (config.isCompositePart && isCompositeTimeSeriesData(traceData)) {
        return { times: traceData.times, data: traceData.data[config.compositeIndex] }
    }

    return traceData as TimeSeriesData
}

export function getGapIndexes(gapIndexes: number[] | number[][], config: TraceDataConfig): number[] {
    if (config.isCompositePart && Array.isArray(gapIndexes[0])) {
        return gapIndexes[config.compositeIndex] as number[]
    }

    return gapIndexes as number[]
}

export function isCompositeTimeSeriesData(traceData: TimeSeriesData | CompositeTimeSeriesData): traceData is CompositeTimeSeriesData {
    return Array.isArray(traceData.data[0]) || traceData.data[0] instanceof Float32Array;
}

export function findIndexOfClosestValidData(timeSeriesData: TimeSeriesData | CompositeTimeSeriesData, minimumTime: number, maximumTime: number, samplingPeriodMs: number, compositeIndex?: number): number {
    minimumTime = Math.max(minimumTime, maximumTime - 2*samplingPeriodMs)
    const times = timeSeriesData.times
    let mostRelevantIndex = -1
    let values: Float32Array

    if (isCompositeTimeSeriesData(timeSeriesData)) {
        if (compositeIndex === undefined || compositeIndex < 0 || compositeIndex >= timeSeriesData.data.length) {
            return -1
        }

        values = timeSeriesData.data[compositeIndex]
    } else {
        values = timeSeriesData.data
    }

    // Start at the last sample at or before the maximum time, so nothing after it is scanned
    // (a picked time early in a page would otherwise walk the whole page on every frame).
    for (let i = bisectRight(times, maximumTime) - 1; i >= 0; i--) {
        // Past the valid window: stop before skipping any more missing values, so a long gap
        // is not walked to the start of the page.
        if (times[i] < minimumTime) {
            return -1
        }

        // Skip values that are undefined or NaN
        if (values[i] === undefined || values[i] === null || isNaN(values[i])) { 
            continue
        }

        // The latest valid sample in the window is the closest to the target time.
        mostRelevantIndex = i
        break
    }

    return mostRelevantIndex
}

// The value at the right edge of the view, or at `atTime` (a time inside the view) when given.
export function getRealtimeValue(dataObjectId: number, dataKey: string, pageManager: TimeSeriesPageManager<any>, traceConfig: TraceDataConfig, viewScale: ScaleTime<any, any, any>, atTime?: number): number | undefined {
    const [currentPage, lastPage] = pageManager.getPagesInView()
    // A picked time can lie outside the view, so only the right-edge reading is kept inside it.
    const startTime = atTime === undefined ? viewScale.domain()[0].getTime() : -Infinity
    const endTime = atTime ?? viewScale.domain()[1].getTime()
    
    let currentValue: number | undefined = undefined

    // A picked time can be off screen: read it from whichever loaded page holds it and the
    // nearest loaded page before it, not only from the pages in view. The page before is found
    // on its own: at an exact page boundary the page holding the time may not be loaded while
    // the one before still has the nearest sample. The two sampling period limit in
    // findIndexOfClosestValidData keeps an older page from answering.
    let pages = [lastPage, currentPage]
    if (atTime !== undefined) {
        const loaded = Array.from(pageManager.getAllLoadedPages().values())
        const holding = loaded.find(page => page.startTime <= atTime && atTime < page.endTime)
        const before = loaded
            .filter(page => page !== holding && page.endTime <= (holding?.startTime ?? atTime))
            .reduce<typeof holding>((nearest, page) => (!nearest || page.endTime > nearest.endTime ? page : nearest), undefined)
        pages = [holding, before]
    }

    for (const page of pages) {
        if (!page || !dataObjectId || !dataKey) {
            continue
        }

        const timeSeriesData = page.data.get(dataObjectId)?.get(dataKey)

        if (!timeSeriesData) {
            continue
        }

        const times = timeSeriesData.times

        if (!times || times.length === 0) {
            continue
        }

        let samplingPeriod: number | undefined = page.timingInformation.get(dataObjectId)?.get(dataKey)?.samplingPeriod

        if (!samplingPeriod && times.length === 1) {
            // It's okay to not have a sampling period when there's only one data point. 
            // We can't calculate the sampling period, but we can still show the realtime value.
            // Just let the sampling period be the page size.
            samplingPeriod = atTime === undefined
                ? endTime - viewScale.domain()[0].getTime()
                : page.endTime - page.startTime // a picked time can be left of the view
        }

        if (!samplingPeriod) {
            continue
        }

        const isComposite = isCompositeTimeSeriesData(timeSeriesData)

        if (isComposite) {
            const trace = traceConfig as CompositePartTraceConfigJSON
            const mostRelevantIndex = findIndexOfClosestValidData(timeSeriesData, startTime, endTime, samplingPeriod, trace.compositeIndex)
            const values = timeSeriesData.data[trace.compositeIndex] as Float32Array
            currentValue = values[mostRelevantIndex]
        } else {
            const mostRelevantIndex = findIndexOfClosestValidData(timeSeriesData, startTime, endTime, samplingPeriod)
            currentValue = timeSeriesData.data[mostRelevantIndex] as number
        }

        if (currentValue !== undefined) {
            break // We already have a value! No need to check on the previous page.
        }
    }

    return currentValue
}


export function getDataInView(dataObjectId: number, pageManger: TimeSeriesPageManager<Page<any, any>>, traceConfig: TraceDataConfig, viewScale: ScaleTime<any, any, any>, allowMissingValues: boolean = false): number[] {
    const [startDate, endDate] = viewScale.domain()
    const startTime = startDate.getTime()
    const endTime = endDate.getTime()
    const pages = pageManger.getPagesInView()

    let result: number[] = []

    // for each page, aggregate the signal from each page, with timestamps, sorted
    for (let i = 0; i < pages.length; i++) {
        const page = pages[i]

        if (!page) {
            continue
        }

        const traceData = page.data.get(dataObjectId)?.get(traceConfig.dataKey)

        if (!traceData) {
            continue
        }

        const startIndex = bisectLeft(traceData.times, startTime)
        const endIndex = bisectRight(traceData.times, endTime)

        let chunk = getSignal(traceData, traceConfig).slice(startIndex, endIndex)

        if (!allowMissingValues) {
            chunk = chunk.filter(value => !Number.isNaN(value))
        }

        result = [...result, ...chunk]
    }

    return result
}
